import { motion } from "framer-motion"; 
import { useState } from "react";

export default function CaseStudies() {
  const [hovered, setHovered] = useState(null);

  const projects = [
    {
      title: "Enterprise Server Upgrade",
      client: "TechCorp",
      description:
        "Migrated and upgraded on-premise servers to enterprise-grade HPE systems, improving performance and uptime by 35%.",
      img: "/Enterprise_Server_Upgrade.png",
    },
    {
      title: "Cloud Migration & Optimization",
      client: "CloudNova",
      description:
        "Successfully migrated workloads to Azure, optimized costs, and implemented secure cloud access policies.",
      img: "/Cloud_Migration_Optimization.png",
    },
    {
      title: "Network Security Overhaul",
      client: "FinSolutions",
      description:
        "Implemented advanced firewalls, monitoring, and endpoint security, reducing potential threats by 90%.",
      img: "/Network_Security.png",
    },
    {
      title: "Virtualization Deployment",
      client: "DataWorks",
      description:
        "Deployed VMware-based virtual environments, consolidating servers and reducing infrastructure costs by 30%.",
      img: "/Virtualization_Deployment.png",
    },
    {
      title: "Backup & Disaster Recovery",
      client: "SecureBank",
      description:
        "Implemented automated backup and disaster recovery solutions using Veeam, ensuring zero data loss.",
      img: "/Backup_Disaster_Recovery.png",
    },
    {
      title: "IT Infrastructure Audit",
      client: "TechSolutions",
      description:
        "Performed comprehensive IT infrastructure audits, identifying risks and optimizing performance across servers and networks.",
      img: "/IT_Infrastructure_Audit.png",
    },
  ];

  return (
    <section className="relative bg-[#0f111a] py-20 px-6 md:px-16 text-white overflow-hidden">
      <div className="max-w-7xl mx-auto text-center">
        {/* Section Title */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-4xl md:text-5xl font-extrabold mb-12 bg-clip-text text-transparent bg-gradient-to-r from-[#00fff7] to-[#0077ff]"
        >
          Case Studies
        </motion.h2>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {projects.map((proj, idx) => (
            <motion.div
              key={idx}
              onMouseEnter={() => setHovered(idx)}
              onMouseLeave={() => setHovered(null)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              animate={{
                scale: hovered === idx ? 1.04 : 1,
                y: hovered === idx ? -4 : 0,
                boxShadow:
                  hovered === idx
                    ? "0px 8px 22px rgba(0, 119, 255, 0.25)"
                    : "0px 4px 12px rgba(0, 0, 0, 0.2)",
              }}
              transition={{
                duration: 0.35,
                ease: [0.23, 1, 0.32, 1],
              }}
              className="relative bg-[#1a1f2b]/80 rounded-2xl overflow-hidden border border-gray-800 hover:border-[#00fff7] transition-all duration-300 cursor-pointer"
            >
              {/* Image */}
              <motion.img
                src={proj.img}
                alt={proj.title}
                className="w-full h-48 object-cover transition-transform duration-700 ease-[0.23,1,0.32,1]"
                style={{
                  transform: hovered === idx ? "scale(1.05)" : "scale(1)",
                }}
              />

              {/* Subtle animated gradient overlay */}
              <motion.div
                className="absolute inset-0 rounded-2xl pointer-events-none bg-gradient-to-r from-[#00fff7] via-[#0077ff] to-[#00fff7] bg-[length:200%_100%] bg-left opacity-10"
                animate={{ backgroundPosition: ["left", "right", "left"] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              />

              {/* Content */}
              <div className="relative z-10 p-6 text-left">
                <h3 className="text-2xl font-semibold text-[#00fff7] mb-2">
                  {proj.title}
                </h3>
                <p className="text-gray-400 italic mb-2">{proj.client}</p>
                <p className="text-gray-300">{proj.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.8, ease: "easeOut" }}
          className="mt-16 text-center"
        >
          <p className="text-gray-300 mb-4 text-lg">
            Want Basore Technologies to handle your next IT project?
          </p>
          <motion.a
            href="#contact"
            className="bg-gradient-to-r from-[#00fff7] to-[#0077ff] text-black font-semibold px-8 py-3 rounded-full shadow-xl hover:scale-105 transition-transform duration-300"
            whileHover={{
              scale: 1.07,
              boxShadow: "0px 6px 18px rgba(0, 119, 255, 0.3)",
            }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
          >
            Get a Consultation
          </motion.a>
        </motion.div>
      </div>

      {/* Background gradient layer */}
      <div className="absolute inset-0 bg-gradient-to-tr from-[#0f111a]/15 via-[#1a1f2b]/10 to-[#0f111a]/15 pointer-events-none -z-10 rounded-2xl"></div>
    </section>
  );
}
